import { useEffect, useState, useRef, useCallback } from 'react';
import { useUser } from '@clerk/nextjs';
import { useMessageService } from './useMessageService';

export const useUnreadMessagesCount = () => {
  const { user } = useUser();
  const { getTotalUnreadMessagesCount, getTotalUnreadMessagesCountForExpert } = useMessageService();
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const getTotalUnreadMessagesCountRef = useRef(getTotalUnreadMessagesCount);
  const getTotalUnreadMessagesCountForExpertRef = useRef(getTotalUnreadMessagesCountForExpert);

  // Keep the refs updated
  useEffect(() => {
    getTotalUnreadMessagesCountRef.current = getTotalUnreadMessagesCount;
    getTotalUnreadMessagesCountForExpertRef.current = getTotalUnreadMessagesCountForExpert;
  }, [getTotalUnreadMessagesCount, getTotalUnreadMessagesCountForExpert]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check if user is an expert
      const expertExternalId = user?.publicMetadata?.expert_external_id as string | undefined;

      if (expertExternalId) {
        // User is an expert - use expert endpoint
        const response = await getTotalUnreadMessagesCountForExpertRef.current(expertExternalId);
        setCount(response.count || 0);
      } else {
        // User is a normal user - use regular endpoint
        const response = await getTotalUnreadMessagesCountRef.current(user.id);
        setCount(response.count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread messages count:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch unread messages count'));
      setCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, user?.publicMetadata?.expert_external_id]);

  useEffect(() => {
    void fetchUnreadCount();

    // Poll every 30 seconds to update the count
    const interval = setInterval(() => {
      void fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return { count, isLoading, error, refetch: fetchUnreadCount };
};
